import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../context/AppContext';
import { Card, CardContent } from './ui/card';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, Area, AreaChart
} from 'recharts';
import { TrendingUp, Loader2, BarChart3, Dumbbell, ArrowDown } from 'lucide-react';
import { toast } from 'sonner';

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const data = payload[0]?.payload;
  if (!data) return null;

  return (
    <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-3 shadow-xl text-xs space-y-1.5">
      <p className="font-heading text-sm uppercase tracking-tight text-white">
        {data.week} <span className="text-zinc-500 font-normal">({data.week_start} - {data.week_end})</span>
      </p>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
        <span className="text-zinc-400">Volume</span>
        <span className="text-orange-400 font-bold text-right">{data.volume}</span>
        <span className="text-zinc-400">Intensite</span>
        <span className="text-blue-400 font-bold text-right">{data.intensity}/5</span>
        <span className="text-zinc-400">Seances</span>
        <span className="text-green-400 font-bold text-right">{data.workouts_done}</span>
        <span className="text-zinc-400">Modificateur</span>
        <span className={`font-bold text-right ${data.volume_modifier > 1 ? 'text-green-400' : data.volume_modifier < 1 ? 'text-amber-400' : 'text-zinc-300'}`}>
          {data.volume_modifier > 1 ? '+' : ''}{Math.round((data.volume_modifier - 1) * 100)}%
        </span>
      </div>
      {data.is_deload && (
        <div className="flex items-center gap-1 text-amber-400 pt-1 border-t border-zinc-700">
          <ArrowDown className="w-3 h-3" />
          <span className="font-bold uppercase text-[10px]">Semaine de decharge</span>
        </div>
      )}
      {data.intensity_level && (
        <p className="text-[10px] text-zinc-500 capitalize">{data.intensity_level}</p>
      )}
    </div>
  );
};

const DeloadDot = (props) => {
  const { cx, cy, payload } = props;
  if (!payload?.is_deload) return null;
  return (
    <g>
      <circle cx={cx} cy={cy} r={6} fill="#f59e0b" fillOpacity={0.3} />
      <circle cx={cx} cy={cy} r={3} fill="#f59e0b" />
    </g>
  );
};

export default function PeriodizationChart({ clientId, language = 'fr' }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const fr = language === 'fr';

  const fetchData = useCallback(async () => {
    try {
      const url = clientId
        ? `/workouts/periodization-chart/${clientId}`
        : '/workouts/periodization-chart';
      const res = await api.get(url);
      setData(res.data.weeks || []);
    } catch (err) {
      if (err.response?.status !== 403) {
        toast.error(fr ? 'Erreur chargement graphique' : 'Chart loading error');
      }
    } finally {
      setLoading(false);
    }
  }, [clientId, fr]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="py-12 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  const hasData = data.some(w => w.volume > 0 || w.workouts_done > 0);
  const totalWorkouts = data.reduce((s, w) => s + w.workouts_done, 0);
  const maxVolume = Math.max(...data.map(w => w.volume), 1);
  const deloadWeeks = data.filter(w => w.is_deload).length;

  // Trend: compare last 2 weeks vs first 2 weeks
  const earlyAvg = data.slice(0, 2).reduce((s, w) => s + w.volume, 0) / 2;
  const lateAvg = data.slice(-2).reduce((s, w) => s + w.volume, 0) / 2;
  const volumeTrend = earlyAvg > 0 ? Math.round(((lateAvg - earlyAvg) / earlyAvg) * 100) : 0;

  return (
    <Card className="bg-card border-border overflow-hidden" data-testid="periodization-chart">
      <CardContent className="p-0">
        {/* Header */}
        <div className="p-4 pb-2 flex items-start justify-between">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-orange-400" />
            <h3 className="font-heading text-lg uppercase tracking-tight text-foreground">
              {fr ? 'Periodisation' : 'Periodization'}
            </h3>
          </div>
          <span className="text-[10px] text-muted-foreground bg-secondary px-2 py-0.5 rounded-full">
            {fr ? '8 semaines' : '8 weeks'}
          </span>
        </div>

        {/* Stats row */}
        <div className="px-4 pb-3 flex gap-3 overflow-x-auto scrollbar-hide">
          <div className="bg-secondary/50 rounded-lg px-3 py-2 flex items-center gap-2 shrink-0">
            <Dumbbell className="w-3.5 h-3.5 text-green-400" />
            <div>
              <p className="text-xs font-bold text-foreground">{totalWorkouts}</p>
              <p className="text-[9px] text-muted-foreground">{fr ? 'Seances' : 'Sessions'}</p>
            </div>
          </div>
          {volumeTrend !== 0 && (
            <div className="bg-secondary/50 rounded-lg px-3 py-2 flex items-center gap-2 shrink-0">
              <TrendingUp className={`w-3.5 h-3.5 ${volumeTrend > 0 ? 'text-green-400' : 'text-red-400'}`} />
              <div>
                <p className={`text-xs font-bold ${volumeTrend > 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {volumeTrend > 0 ? '+' : ''}{volumeTrend}%
                </p>
                <p className="text-[9px] text-muted-foreground">{fr ? 'Vol. tendance' : 'Vol. trend'}</p>
              </div>
            </div>
          )}
          {deloadWeeks > 0 && (
            <div className="bg-secondary/50 rounded-lg px-3 py-2 flex items-center gap-2 shrink-0">
              <ArrowDown className="w-3.5 h-3.5 text-amber-400" />
              <div>
                <p className="text-xs font-bold text-amber-400">{deloadWeeks}</p>
                <p className="text-[9px] text-muted-foreground">{fr ? 'Decharges' : 'Deloads'}</p>
              </div>
            </div>
          )}
        </div>

        {!hasData ? (
          <div className="px-4 pb-6 text-center py-8">
            <Dumbbell className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">
              {fr ? 'Pas encore de données. Commence a loguer tes entraînements!' : 'No data yet. Start logging your workouts!'}
            </p>
          </div>
        ) : (
          <div className="px-2 pb-4">
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={data} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                <defs>
                  <linearGradient id="volumeGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#f97316" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#f97316" stopOpacity={0.02} />
                  </linearGradient>
                  <linearGradient id="intensityGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.2} />
                    <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis
                  dataKey="week"
                  tick={{ fill: '#71717a', fontSize: 11 }}
                  axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                  tickLine={false}
                />
                <YAxis
                  yAxisId="volume"
                  tick={{ fill: '#71717a', fontSize: 10 }}
                  axisLine={false}
                  tickLine={false}
                  domain={[0, 'auto']}
                />
                <YAxis
                  yAxisId="intensity"
                  orientation="right"
                  tick={{ fill: '#71717a', fontSize: 10 }}
                  axisLine={false}
                  tickLine={false}
                  domain={[0, 5]}
                />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  yAxisId="volume"
                  type="monotone"
                  dataKey="volume"
                  stroke="#f97316"
                  strokeWidth={2.5}
                  fill="url(#volumeGrad)"
                  dot={{ fill: '#f97316', r: 3, strokeWidth: 0 }}
                  activeDot={{ r: 5, fill: '#f97316', stroke: '#000', strokeWidth: 2 }}
                  name="Volume"
                />
                <Line
                  yAxisId="intensity"
                  type="monotone"
                  dataKey="intensity"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={<DeloadDot />}
                  activeDot={{ r: 5, fill: '#3b82f6', stroke: '#000', strokeWidth: 2 }}
                  name="Intensité"
                />
              </AreaChart>
            </ResponsiveContainer>

            {/* Legend */}
            <div className="flex items-center justify-center gap-4 mt-2">
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-0.5 bg-orange-500 rounded-full" />
                <span className="text-[10px] text-zinc-500">{fr ? 'Volume (reps totales)' : 'Volume (total reps)'}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-0.5 bg-blue-500 rounded-full" style={{ borderBottom: '1px dashed #3b82f6' }} />
                <span className="text-[10px] text-zinc-500">{fr ? 'Intensite (/5)' : 'Intensity (/5)'}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-amber-500" />
                <span className="text-[10px] text-zinc-500">{fr ? 'Decharge' : 'Deload'}</span>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
